import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSearchPodioIconsQuery } from "@/store/rest_api";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  defaultQuery?: string;
  onSelect: (iconId: number) => void;
  isSubmitting?: boolean;
}

export const podioIconUrl = (id: number) =>
  `https://d2cmuesa4snpwn.cloudfront.net/images/emails/app-icons/${id}.png`;

export default function PodioIconPicker({
  open,
  onOpenChange,
  title,
  description,
  defaultQuery = "",
  onSelect,
  isSubmitting,
}: Props) {
  const [query, setQuery] = useState(defaultQuery);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const trimmedQuery = query.trim();
  const { data: searchedIcons, isFetching } = useSearchPodioIconsQuery(trimmedQuery, {
    skip: !open || trimmedQuery.length === 0,
  });
  const allIcons = React.useMemo(() => Array.from({ length: 700 }, (_, index) => index + 1), []);
  const icons = trimmedQuery.length === 0 ? allIcons : searchedIcons;

  const handleConfirm = () => {
    if (selectedId !== null) {
      onSelect(selectedId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <Input
          placeholder="Search icons (e.g. phone, person, call)"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className="h-80 overflow-y-auto border rounded p-2">
          {isFetching && (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <Loader2 className="animate-spin mr-2" /> Loading icons...
            </div>
          )}
          {!isFetching && icons && icons.length === 0 && (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              No icons found
            </div>
          )}
          {!isFetching && icons && icons.length > 0 && (
            <div className="grid grid-cols-8 gap-2">
              {icons.map((iconId) => (
                <button
                  key={iconId}
                  type="button"
                  onClick={() => setSelectedId(iconId)}
                  className={`aspect-square rounded border-2 p-1 flex items-center justify-center hover:bg-muted transition ${
                    selectedId === iconId ? "border-primary" : "border-transparent"
                  }`}
                  title={`Icon ${iconId}`}
                >
                  <img src={podioIconUrl(iconId)} alt={`icon ${iconId}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedId === null || isSubmitting}>
            {isSubmitting ? "Creating..." : "Create app"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
